import { useCallback, useContext, useState } from 'react';
import { SplitEvaluationContext } from './contex';
import {
  EvaluationScheduleFormType,
  SplitEvaluationSchemaType,
} from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { routes } from 'router/routes';

/**
 * Simple wrapper for the two-step scheduling of split evaluations. Will save first evaluation then when second arrives, will connect the result to the main context
 */

export const SplitEvaluationsScheduleSection = () => {
  const { onContinueToEvalForm, evaluationSchedule } = useSignupDirectCtx();
  const [firstTimeSlot, setFirstTimeSlot] = useState<SplitEvaluationSchemaType['firstTimeSlot'] | undefined>(
    getDefaultFirstSlot(evaluationSchedule),
  );
  const { displayTimezone } = useDisplayTimezone();
  const navigate = useNavigate();

  const onSubmitSecondSlot = useCallback(
    (secondTimeSlot: SplitEvaluationSchemaType['secondTimeSlot']) => {
      if (!firstTimeSlot) throw new Error('Expected first time slot to be set');
      if (!displayTimezone) throw new Error('Expected display timezone to be set');

      onContinueToEvalForm({
        type: 'split',
        firstTimeSlot,
        secondTimeSlot,
        timeZone: displayTimezone.resolvedName,
      });
    },
    [firstTimeSlot, displayTimezone, onContinueToEvalForm],
  );

  const onSkipSecondSlot = useCallback(() => {
    if (!firstTimeSlot) throw new Error('Expected first time slot to be set');
    if (!displayTimezone) throw new Error('Expected display timezone to be set');

    onContinueToEvalForm({
      type: 'split',
      firstTimeSlot,
      timeZone: displayTimezone.resolvedName,
    });
  }, [firstTimeSlot, displayTimezone, onContinueToEvalForm]);

  const onSubmitFirstSlot = useCallback((timeSlot: SplitEvaluationSchemaType['firstTimeSlot']) => {
    setFirstTimeSlot(timeSlot);
    navigate(routes.signupDirect.evaluation.schedule.split.second);
  }, []);

  return (
    <SplitEvaluationContext.Provider value={{ onSubmitFirstSlot, onSubmitSecondSlot, firstTimeSlot, onSkipSecondSlot }}>
      <Outlet />
    </SplitEvaluationContext.Provider>
  );
};

const getDefaultFirstSlot = (evaluationSchedule?: EvaluationScheduleFormType) => {
  if (evaluationSchedule?.type === 'split') {
    return evaluationSchedule.firstTimeSlot;
  }
};

export const useSplitEvaluationCtx = () => {
  const ctx = useContext(SplitEvaluationContext);
  if (!ctx) throw new Error('Expected SplitEvaluationContext to be set');
  return ctx;
};
